import React from 'react'
import { components as selectComponents } from 'react-select'
import AsyncSelect from 'react-select/async'
import styled, { css } from 'styled-components'
import { useUSSchoolsMutation } from '@mote/api'
import { CloseIcon, SearchIcon } from '@mote/assets/icons'
import { useGoalsState } from '@mote/helpers/hooks/business'
import { reallyTruthy } from '@mote/helpers/validation'
import { Block, Button, H5, Input, P, Select, Switch, Text } from '@mote/ui'
import { useWizard } from '@mote/ui'
import {
  MOTE_DEBUG_US_SCHOOLS_LABS_MODE_STORAGE_KEY,
  MOTE_DEBUG_US_SCHOOLS_STORAGE_KEY
} from '../../Admin/AdminDebugSAKScreen'
import { useOnboarding } from '../context'
import { Card } from './Card'

const US_STATES = [
  {
    text: 'AL',
    value: 'AL'
  },
  {
    text: 'AK',
    value: 'AK'
  },
  {
    text: 'AS',
    value: 'AS'
  },
  {
    text: 'AZ',
    value: 'AZ'
  },
  {
    text: 'AR',
    value: 'AR'
  },
  {
    text: 'CA',
    value: 'CA'
  },
  {
    text: 'CO',
    value: 'CO'
  },
  {
    text: 'CT',
    value: 'CT'
  },
  {
    text: 'DE',
    value: 'DE'
  },
  {
    text: 'DC',
    value: 'DC'
  },
  {
    text: 'FM',
    value: 'FM'
  },
  {
    text: 'FL',
    value: 'FL'
  },
  {
    text: 'GA',
    value: 'GA'
  },
  {
    text: 'GU',
    value: 'GU'
  },
  {
    text: 'HI',
    value: 'HI'
  },
  {
    text: 'ID',
    value: 'ID'
  },
  {
    text: 'IL',
    value: 'IL'
  },
  {
    text: 'IN',
    value: 'IN'
  },
  {
    text: 'IA',
    value: 'IA'
  },
  {
    text: 'KS',
    value: 'KS'
  },
  {
    text: 'KY',
    value: 'KY'
  },
  {
    text: 'LA',
    value: 'LA'
  },
  {
    text: 'ME',
    value: 'ME'
  },
  {
    text: 'MH',
    value: 'MH'
  },
  {
    text: 'MD',
    value: 'MD'
  },
  {
    text: 'MA',
    value: 'MA'
  },
  {
    text: 'MI',
    value: 'MI'
  },
  {
    text: 'MN',
    value: 'MN'
  },
  {
    text: 'MS',
    value: 'MS'
  },
  {
    text: 'MO',
    value: 'MO'
  },
  {
    text: 'MT',
    value: 'MT'
  },
  {
    text: 'NE',
    value: 'NE'
  },
  {
    text: 'NV',
    value: 'NV'
  },
  {
    text: 'NH',
    value: 'NH'
  },
  {
    text: 'NJ',
    value: 'NJ'
  },
  {
    text: 'NM',
    value: 'NM'
  },
  {
    text: 'NY',
    value: 'NY'
  },
  {
    text: 'NC',
    value: 'NC'
  },
  {
    text: 'ND',
    value: 'ND'
  },
  {
    text: 'MP',
    value: 'MP'
  },
  {
    text: 'OH',
    value: 'OH'
  },
  {
    text: 'OK',
    value: 'OK'
  },
  {
    text: 'OR',
    value: 'OR'
  },
  {
    text: 'PW',
    value: 'PW'
  },
  {
    text: 'PA',
    value: 'PA'
  },
  {
    text: 'PR',
    value: 'PR'
  },
  {
    text: 'RI',
    value: 'RI'
  },
  {
    text: 'SC',
    value: 'SC'
  },
  {
    text: 'SD',
    value: 'SD'
  },
  {
    text: 'TN',
    value: 'TN'
  },
  {
    text: 'TX',
    value: 'TX'
  },
  {
    text: 'UT',
    value: 'UT'
  },
  {
    text: 'VT',
    value: 'VT'
  },
  {
    text: 'VI',
    value: 'VI'
  },
  {
    text: 'VA',
    value: 'VA'
  },
  {
    text: 'WA',
    value: 'WA'
  },
  {
    text: 'WV',
    value: 'WV'
  },
  {
    text: 'WI',
    value: 'WI'
  },
  {
    text: 'WY',
    value: 'WY'
  }
]

export const SchoolAnswer = () => {
  const { isNextDisabled, toggleNext } = useWizard()
  const { state, updateState } = useOnboarding()
  const [enterUSSchoolManually, setEnterUSSchoolManually] =
    React.useState(false)
  const goalsState = useGoalsState()

  const hasHints = React.useMemo(
    () =>
      Array.isArray(state.institutionNameHints) &&
      state.institutionNameHints.length > 0,
    [state.institutionNameHints]
  )
  const isUS = React.useMemo(() => {
    const isEducator = state.role === 'educator'
    const rolesPassed = !['administrator', 'other'].includes(state.role)
    const educatorPassed =
      !isEducator ||
      (isEducator &&
        !state.survey.teaches_grade.some(({ value }) =>
          ['other', 'higher_ed'].includes(value)
        ))

    return (
      reallyTruthy(
        localStorage.getItem(MOTE_DEBUG_US_SCHOOLS_STORAGE_KEY) ||
          goalsState.canSchoolSearchDuringUserOnboarding
      ) &&
      rolesPassed &&
      educatorPassed
    )
  }, [])

  React.useEffect(() => {
    const shouldProceed =
      state.survey.usSchool || state.survey.institutionName !== ''

    if (
      (shouldProceed && isNextDisabled) ||
      (!shouldProceed && !isNextDisabled)
    )
      toggleNext()
  }, [
    state.survey.usSchool,
    state.survey.institutionName,
    isNextDisabled,
    toggleNext
  ])

  function handleInputChange(event) {
    const {
      target: { value }
    } = event
    const survey = state.survey
    survey.institutionName = value

    updateState({ survey })
  }

  return (
    <>
      {!isUS && <NonUSCountry hasHints={hasHints} />}
      {isUS && (
        <Block gap={16}>
          <USCountry disabled={enterUSSchoolManually} />
          <Block gap={16}>
            <Text fontSize={15} fontWeight={500} color='#25252D'>
              Can't find your school?{' '}
              <Button
                variant='link'
                text={enterUSSchoolManually ? 'Cancel' : 'Enter manually'}
                onClick={() => setEnterUSSchoolManually(prev => !prev)}
              />
            </Text>

            {enterUSSchoolManually && (
              <NonUSSchoolCard hasHints={false}>
                <StyledInput
                  value={state.survey.institutionName}
                  onChange={handleInputChange}
                  placeholder={'Enter your school name'}
                  hasHints={false}
                />
              </NonUSSchoolCard>
            )}
          </Block>
        </Block>
      )}
    </>
  )
}

const NonUSCountry = ({ hasHints }) => {
  const { state, updateState } = useOnboarding()
  const [selectedHint, setSelectedHint] = React.useState(
    state.survey.institutionName
  )
  const hasHint = React.useMemo(
    () =>
      hasHints &&
      Boolean(
        state.institutionNameHints.find(
          hint => hint === state.survey.institutionName
        )
      ),
    [hasHints, state.institutionNameHints, state.survey.institutionName]
  )

  function handleInputChange(event) {
    const {
      target: { value }
    } = event
    const survey = state.survey
    survey.institutionName = value

    updateState({ survey })
  }

  function handleHintSelection(hint) {
    const survey = state.survey
    survey.institutionName = hint

    setSelectedHint(hint)
    updateState({ survey })
  }

  function handleHintClear() {
    const survey = state.survey
    survey.institutionName = ''

    setSelectedHint(null)
    updateState({ survey })
  }

  return (
    <NonUSSchoolCard hasHints={hasHints}>
      <Block layout='vertical' justify='center' spreadX spreadY>
        {hasHint && (
          <HintContainer>
            <Hint text={selectedHint} onClear={handleHintClear} isClearable />
          </HintContainer>
        )}
        {!hasHint && (
          <StyledInput
            value={state.survey.institutionName}
            onChange={handleInputChange}
            placeholder={
              state.survey.role[0].value === 'other'
                ? 'Tell us your organisation name'
                : 'Start typing...'
            }
            hasHints={hasHints}
          />
        )}
        {hasHints && (
          <SchoolHints
            selectedHint={selectedHint}
            onHintSelection={handleHintSelection}
          />
        )}
      </Block>
    </NonUSSchoolCard>
  )
}

const USCountry = ({ disabled = false }) => {
  const { state, updateState } = useOnboarding()
  const [selectedValue, setSelectedValue] = React.useState(null)
  const [defaultOptions, setDefaultOptions] = React.useState(null)
  const [networkBusy, setNetworkBusy] = React.useState()
  const [networkError, setNetworkError] = React.useState()
  const [stateOverride, setStateOverride] = React.useState('')
  const [disableGeoIp, setDisableGeoIp] = React.useState(false)
  const { mutate: getSchools } = useUSSchoolsMutation()
  const isUSDebugMode = React.useMemo(
    () =>
      reallyTruthy(
        localStorage.getItem(MOTE_DEBUG_US_SCHOOLS_LABS_MODE_STORAGE_KEY)
      ),
    []
  )

  const loadOptions = React.useCallback(
    text => {
      if (!text.length) {
        reset()
        return
      }

      setNetworkBusy(true)
      return getSchools({
        variables: {
          searchTerm: text,
          stateOverride,
          disableGeoIp
        }
      })
        .then(({ data: response }) => {
          if (!response) return

          const data = response.search?.matches

          setNetworkBusy(false)
          setDefaultOptions(data)

          return data
        })
        .catch(e => {
          console.error(e)
          setNetworkBusy(false)
          setNetworkError('Something bad happened')
        })
    },
    [stateOverride, disableGeoIp, getSchools]
  )

  function reset() {
    const survey = state.survey

    if (survey.usSchool) {
      delete survey.usSchool
    }

    setNetworkError(false)
    setNetworkBusy(false)
    setDefaultOptions(null)
    setSelectedValue(null)
    updateState({ survey })
  }

  function handleChange(data) {
    if (!data) {
      reset()
      return
    }

    const { forSubmission } = data
    const survey = state.survey
    survey.usSchool = forSubmission

    setSelectedValue(data)
    updateState({ survey })
  }

  return (
    <USSchoolCard disabled={disabled}>
      <StyledSelect
        disabled={disabled}
        cacheOptions
        isClearable
        defaultOptions={defaultOptions}
        placeholder='Search by ZIP or School name'
        noOptionsMessage={() => 'No results...'}
        value={selectedValue}
        getOptionLabel={option => {
          if (!option) return ''

          const {
            forDisplay: { schoolName }
          } = option

          return schoolName
        }}
        getOptionValue={option => option}
        loadOptions={loadOptions}
        onChange={handleChange}
      />
      {isUSDebugMode && (
        <DebugUSSchool
          debugState={{
            stateOverride,
            networkBusy,
            disableGeoIp,
            networkError
          }}
          updateState={{ setDisableGeoIp, setStateOverride }}
        />
      )}
    </USSchoolCard>
  )
}

const StyledSelectControl = ({ children, ...props }) => {
  return (
    <selectComponents.Control {...props}>
      <Block layout='horizontal' align='center' gap={4} maxHeight={26} spreadX>
        <SearchIcon color='currentColor' />
        {children}
      </Block>
    </selectComponents.Control>
  )
}

const StyledSelectClearIndicator = props => {
  const {
    getStyles,
    innerProps: { ref, ...restInnerProps }
  } = props
  return (
    <div
      {...restInnerProps}
      ref={ref}
      style={getStyles('clearIndicator', props)}
    >
      <ClearBtn>
        <CloseIcon color='currentColor' />
      </ClearBtn>
    </div>
  )
}

const StyledSelect = props => {
  const styles = {
    option: (styles, { isFocused, isSelected }) => {
      return {
        ...styles,
        fontSize: '15px',
        backgroundColor: isSelected
          ? '#ac0ae8'
          : isFocused
          ? '#F6F6F9'
          : undefined,
        color: isSelected ? '#ffffff' : '#25252D',
        cursor: 'pointer',
        ':active': {
          ...styles[':active'],
          color: isSelected ? '#ffffff' : '#9b09d1',
          backgroundColor: isSelected ? '#ac0ae8' : '#F6F6F9'
        }
      }
    },
    control: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: props.disabled ? '#ececf3' : '#ffffff',
      pointerEvents: props.disabled ? 'none' : 'auto',
      color: props.disabled ? 'rgb(37 37 45 / 30%)' : '#25252D',
      cursor: 'pointer',
      borderRadius: '8px',
      borderColor: isFocused ? '#ac0ae8' : 'transparent',
      boxShadow: isFocused ? '0 0 0 1px #ac0ae8' : 'none',
      padding: '20px',
      fontSize: '15px',
      lineHeight: '120%',
      fontFamily: "'DM Sans', sans-serif",
      fontWeight: 'normal',
      transition: 'all 150ms ease-in',
      ':hover': {
        ...styles[':hover'],
        borderColor: isFocused ? '#ac0ae8' : 'transparent'
      }
    }),
    placeholder: styles => ({
      ...styles,
      color: props.disabled ? 'rgb(37 37 45 / 30%)' : '#B2B2CC'
    }),
    singleValue: styles => ({
      ...styles,
      color: 'inherit',
      fontSize: 'inherit',
      fontFamily: 'inherit',
      lineHeight: 'inherit',
      fontWeight: 'inherit'
    }),
    menu: styles => ({
      ...styles,
      zIndex: 20,
      border: '1px solid rgba(0, 0, 0, 0.1)',
      padding: '8px 0px',
      borderRadius: '8px',
      boxShadow:
        '0px 1px 3px rgba(113, 101, 129, 0.04), 0px 3px 14px rgba(113, 101, 129, 0.08), 0px 8px 32px rgba(113, 101, 129, 0.1)',
      background: '#ffffff',
      transition: 'all 150ms ease-in'
    }),
    indicatorsContainer: styles => ({
      ...styles,
      color: '#25252D'
    }),
    container: styles => ({
      ...styles,
      width: '100%'
    }),
    noOptionsMessage: styles => ({
      ...styles,
      textAlign: 'left'
    }),
    loadingMessage: styles => ({
      ...styles,
      textAlign: 'left'
    }),
    valueContainer: styles => ({ ...styles, padding: 0 })
  }

  return (
    <AsyncSelect
      styles={styles}
      components={{
        ...selectComponents,
        Control: StyledSelectControl,
        ClearIndicator: StyledSelectClearIndicator,
        IndicatorSeparator: () => null,
        DropdownIndicator: () => null
      }}
      {...props}
    />
  )
}

const DebugUSSchool = ({ debugState, updateState }) => {
  const networkStatus = debugState.networkError
    ? 'error'
    : debugState.networkBusy
    ? 'busy'
    : 'idle'
  function handleChange(method, value) {
    updateState[`set${method}`](value)
  }

  return (
    <RootDebugSchool
      layout='vertical'
      align='flex-start'
      justify='flex-start'
      gap={12}
      spreadX
    >
      <Block layout='horizontal' align='center' gap={8}>
        <P color='#25252D' fontSize={15} lineHeight='100%' fontWeight='normal'>
          Disable GeoIP:
        </P>
        <Switch
          checked={debugState.disableGeoIp}
          onChange={event => handleChange('DisableGeoIp', event.target.checked)}
        />
      </Block>
      <Block layout='horizontal' align='center' gap={6}>
        <P color='#25252D' fontSize={15} lineHeight='100%' fontWeight='normal'>
          State:
        </P>
        <NarrowSelect
          currentValue={debugState.stateOverride}
          options={US_STATES}
          placeholder='Select...'
          onChange={event => handleChange('StateOverride', event.target.value)}
        />
      </Block>
      <P color='#25252D' fontSize={15} lineHeight='100%' fontWeight='normal'>
        Network status:{' '}
        <NetworkStatus status={networkStatus}>
          {debugState.networkError ?? debugState.networkBusy ? 'Busy' : 'Idle'}
        </NetworkStatus>
      </P>
    </RootDebugSchool>
  )
}

const SchoolHints = ({ selectedHint, onHintSelection }) => {
  const { state } = useOnboarding()
  const hints = React.useMemo(
    () => state.institutionNameHints?.filter(hint => hint !== selectedHint),
    [selectedHint]
  )

  if (hints.length === 0) return null

  return (
    <HintsContainer layout='vertical' gap={16}>
      <H5 color='#6C6C84' fontSize={15} lineHeight='140%' fontWeight={400}>
        Suggested based on your domain:
      </H5>
      <Block layout='horizontal' align='center' gap={16} wrapped>
        {hints.map((hint, index) => {
          return (
            <Hint
              key={`${hint}-${index}`}
              onClick={() => onHintSelection(hint)}
              text={hint}
            />
          )
        })}
      </Block>
    </HintsContainer>
  )
}

const Hint = ({ text, isClearable, onClear, onClick, ...rest }) => {
  return (
    <RootHint
      role='button'
      layout='horizontal'
      align='center'
      justify='space-between'
      gap={8}
      isClearable={isClearable}
      onClick={!isClearable ? onClick : undefined}
      {...rest}
    >
      <HintText>{text}</HintText>
      {isClearable && onClear && (
        <ClearBtn onClick={onClear}>
          <CloseIcon color='currentColor' />
        </ClearBtn>
      )}
    </RootHint>
  )
}

const RootDebugSchool = styled(Block)`
  margin-top: 16px;
  padding: 0 24px 24px 24px;

  @media (max-width: 639px) {
    padding: 0 16px 16px 16px;
  }
`

const StyledCard = styled(Card)`
  min-height: 40px;
`

const USSchoolCard = styled(StyledCard)`
  padding: 0 !important;
  border: none;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}
`

const NonUSSchoolCard = styled(StyledCard)`
  padding: 0 !important;
`

const HintsContainer = styled(Block)`
  border-top: 1px solid #ececf3;
  padding: 24px;

  @media (max-width: 639px) {
    padding: 16px;
  }
`

const HintContainer = styled(Block)`
  padding: 24px;

  @media (max-width: 639px) {
    padding: 16px;
  }
`

const RootHint = styled(Block)`
  color: #25252d;
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 100%;
  padding: 12.5px 16px;
  background: #f6f6f9;
  border-radius: 4px;
  width: max-content;
  height: 40px;

  ${({ isClearable }) =>
    !isClearable
      ? css`
          cursor: pointer;

          &:hover {
            background: #ececf3;
          }
        `
      : css`
          cursor: default;
        `};
`

const HintText = styled.span`
  line-height: 135%;
`

const ClearBtn = styled.button`
  color: #25252d;
  display: flex;
  flex: 0 0 auto;
  outline: none;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  touch-action: manipulation;
  border-radius: 4px;
  border: none;
  height: 40px;
  width: 22px;
  font-size: 15px;
  line-height: 100%;
  appearance: none;
  user-select: none;
  margin-right: -5px;

  &:hover {
    color: #9b09d1;
  }

  &:active {
    color: #7807a2;
  }

  &:focus {
    outline: none;
  }
`

const StyledInput = styled(Input)`
  & .mote-input-wrapper {
    height: unset;
    border-radius: 8px;
  }

  & .mote-input-group {
    border-radius: 8px;
    border-color: transparent;
    background: transparent !important;

    &:hover:not([data-focused]) {
      border-color: transparent;
    }

    &[data-focused] {
      border-color: #ac0ae8;
    }
  }

  & .mote-input {
    margin: 0;
    padding: 0;
  }

  ${({ hasHints }) =>
    hasHints
      ? css`
          & .mote-input-group {
            padding: 33px 24px;
            min-height: 40px;
            z-index: 1;

            @media (max-width: 639px) {
              padding: 25px 16px;
            }
          }
        `
      : css`
          & .mote-input-group {
            padding: 24px;

            @media (max-width: 639px) {
              padding: 19px 16px;
            }
          }
        `};
`

const NetworkStatus = styled.span`
  color: ${({ status }) => {
    switch (status) {
      case 'error':
        return '#F44336'
      case 'busy':
        return '#F5BC00'
      case 'idle':
        return '#57C96A'
      default:
        return '#25252D'
    }
  }};
`

const NarrowSelect = styled(Select)`
  height: 28px;
`
